import { KeyboardEvent, ReactNode, useSyncExternalStore } from 'react';

import { AccountSection, accountPanelId, accountTabId } from '~/utils/accountSections';
import styles from './AccountMenu.module.css';
import { Svg } from './Svg';

/** Même seuil que `@media (max-width: 760px)` des styles de Mon compte (liste puis rubrique). */
const PHONE_QUERY = '(max-width: 760px)';
const subscribePhone = (onChange: () => void) => {
    const query = window.matchMedia(PHONE_QUERY);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
};
const isPhoneLayout = () => window.matchMedia(PHONE_QUERY).matches;

export type AccountMenuItem = {
    id: AccountSection;
    label: string;
    /** Une ligne sous le titre : ce qu'on y trouve, ou l'état de l'abonnement. */
    hint: string;
    icon: ReactNode;
    badge?: string;
};

/**
 * Menu des rubriques de Mon compte : colonne à gauche sur ordinateur, liste plein
 * écran sur téléphone (la rubrique s'ouvre ensuite à la place). Motif ARIA « tabs »
 * vertical à activation manuelle : flèches haut/bas, Début/Fin déplacent le focus,
 * Entrée / Espace / un clic ouvre la rubrique. `atMenu` : aucune rubrique choisie ; sur
 * téléphone (liste seule à l'écran), aucune ne paraît alors ouverte.
 */
export function AccountMenu({
    items,
    active,
    atMenu,
    label,
    onSelect,
}: {
    items: AccountMenuItem[];
    active: AccountSection;
    atMenu: boolean;
    label: string;
    onSelect: (id: AccountSection) => void;
}) {
    const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
        const current = items.findIndex((item) => item.id === (e.target as HTMLElement).id.replace('account-tab-', ''));
        const index = current >= 0 ? current : 0;
        const target = {
            ArrowDown: (index + 1) % items.length,
            ArrowUp: (index - 1 + items.length) % items.length,
            Home: 0,
            End: items.length - 1,
        }[e.key];
        if (target === undefined) return;
        e.preventDefault();
        document.getElementById(accountTabId(items[target].id))?.focus();
    };

    // Liste seule à l'écran (téléphone, rien de choisi) : aucune rubrique « sélectionnée ».
    const listOnly = useSyncExternalStore(subscribePhone, isPhoneLayout) && atMenu;
    const shown = !listOnly && items.some((item) => item.id === active) ? active : null;

    return (
        <div role="tablist" aria-label={label} aria-orientation="vertical" className={styles.list}>
            {items.map((item, i) => {
                const selected = item.id === shown;
                // Rien de sélectionné (liste du téléphone, ou rubrique pas encore dans le menu
                // pendant la lecture de l'abonnement) : la première reste atteignable au clavier.
                const reachable = selected || (i === 0 && shown === null);
                return (
                    <button
                        key={item.id}
                        id={accountTabId(item.id)}
                        type="button"
                        role="tab"
                        aria-selected={selected}
                        aria-controls={accountPanelId(item.id)}
                        tabIndex={reachable ? 0 : -1}
                        className={`${styles.item} ${selected ? styles.active : ''}`}
                        onClick={() => onSelect(item.id)}
                        onKeyDown={onKeyDown}
                    >
                        <span className={styles.icon}>{item.icon}</span>
                        <span className={styles.texts}>
                            <span className={styles.label}>
                                {item.label}
                                {item.badge && <em className={styles.badge}>{item.badge}</em>}
                            </span>
                            <span className={styles.hint}>{item.hint}</span>
                        </span>
                        <Svg className={styles.chevron} aria-hidden="true">
                            <path d="m9 18 6-6-6-6" />
                        </Svg>
                    </button>
                );
            })}
        </div>
    );
}
